import React, { createContext, useContext, useEffect, useState } from "react";
import { auth } from "../lib/Firebase-config";
import {
	createUserWithEmailAndPassword,
	signInWithEmailAndPassword,
	signOut,
	onAuthStateChanged,
	updateProfile,
} from "firebase/auth";
import { getFirestore, doc, setDoc, onSnapshot } from "firebase/firestore";
import fb from "../lib/Firebase-config";
import { createDefaultSubscription } from "../config/tierConfig";

const UserContext = createContext();
const db = getFirestore(fb);

export const AuthContextProvider = ({ children }) => {
	const [user, setUser] = useState(null);
	const [userName, setUserName] = useState("");
	const [isAdmin, setIsAdmin] = useState(false);
	const [aiEnabled, setAiEnabled] = useState(false);
	const [loading, setLoading] = useState(true);

	const buildUserState = (currentUser, extra = {}) => ({
		uid: currentUser.uid,
		email: currentUser.email,
		displayName: currentUser.displayName,
		photoURL: currentUser.photoURL,
		metadata: currentUser.metadata,
		...extra
	});

	const createUser = async (email, password, displName) => {
		const userCredential = await createUserWithEmailAndPassword(auth, email, password);
		const user = userCredential.user;
		await updateProfile(user, { displayName: displName });

			await setDoc(doc(db, 'users', user.uid), {
				email: email,
				displayName: displName,
				createdAt: new Date(),
				updatedAt: new Date(),
				role: 'student',
				subscription: createDefaultSubscription()
			}, { merge: true });
		};

	const signIn = (email, password) => {
		return signInWithEmailAndPassword(auth, email, password);
	};

	const logout = () => {
		setUserName("");
		setIsAdmin(false);
		setAiEnabled(false);
		return signOut(auth);
	};

	useEffect(() => {
		let unsubscribeUserDoc = null;

		const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
			// Clean up previous listener if exists (e.g. user switch or logout)
			if (unsubscribeUserDoc) {
				unsubscribeUserDoc();
				unsubscribeUserDoc = null;
			}

				if (currentUser) {
					let tokenClaims = {};
					try {
						const tokenResult = await currentUser.getIdTokenResult();
						tokenClaims = tokenResult.claims || {};
					} catch (error) {
						console.warn("Auth token claims warning:", error.message);
					}

					// Fetch extra user data
					const userRef = doc(db, 'users', currentUser.uid);

					// Real-time listener for admin/AI status changes
					unsubscribeUserDoc = onSnapshot(userRef, (docSnap) => {
						const claimAdmin = tokenClaims.admin === true || tokenClaims.isAdmin === true;

						if (docSnap.exists()) {
							const data = docSnap.data();
							const docAdmin = data.isAdmin === true;
							const nextIsAdmin = claimAdmin || docAdmin;
							const nextAiEnabled = nextIsAdmin || data.aiEnabled === true;

							setIsAdmin(nextIsAdmin);
							setAiEnabled(nextAiEnabled);
							setUser(buildUserState(currentUser, {
								isAdmin: nextIsAdmin,
								aiEnabled: nextAiEnabled,
								profileExists: true
							}));
						} else {
							setIsAdmin(claimAdmin);
							setAiEnabled(claimAdmin);
							setUser(buildUserState(currentUser, {
								isAdmin: claimAdmin,
								aiEnabled: claimAdmin,
								profileExists: false
							}));
						}
						setLoading(false);
					}, (error) => {
						console.warn("Auth snapshot listener warning:", error.message);
						const claimAdmin = tokenClaims.admin === true || tokenClaims.isAdmin === true;
						setIsAdmin(claimAdmin);
						setAiEnabled(claimAdmin);
						setUser(buildUserState(currentUser, {
							isAdmin: claimAdmin,
							aiEnabled: claimAdmin,
							profileExists: null
						}));
						setLoading(false);
					});
				} else {
					setUser(null);
					setIsAdmin(false);
					setAiEnabled(false);
					setLoading(false);
				}
			});
		return () => {
			if (unsubscribeUserDoc) unsubscribeUserDoc();
			unsubscribeAuth();
		};
	}, []);

	return (
			<UserContext.Provider
				value={{ createUser, user, userName, logout, signIn, isAdmin, aiEnabled, loading }}
			>
			{children}
		</UserContext.Provider>
	);
};

export const UserAuth = () => {
	return useContext(UserContext);
};
